/**
 * AuthApp Component - Full Application
 * خالتي هيلة - التطبيق الكامل
 * Mobile First + جميع التعديلات
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Heart, MessageCircle, Send, Sparkles, ArrowRight, ArrowLeft, Check, Lock, 
  Mail, AlertCircle, User, Phone, X, LogOut, RefreshCw, Home, Settings,
  Clock, Star, Camera, Users, Share2, Copy, CheckCircle, XCircle,
  Calendar, MapPin, Briefcase, ChevronLeft, ChevronRight, Flag, Ban,
  Bell, Eye, HelpCircle, Loader, ArrowDown
} from 'lucide-react';
import { auth, db } from '../firebase';
import { generateMatchRecommendations, generateConversationSuggestions, analyzeDealbreaker } from '../aiService';
import { sendNewMessageNotification, sendContactSharedNotification } from '../emailService';
import {
  encryptData, decryptData, sanitizeInput, isValidEmail, isValidSaudiPhone,
  formatPhone, isValidAge, authRateLimiter, messageRateLimiter,
  checkFullCompatibility, calculateCompatibilityScore, getAuthErrorMessage, 
  logSecurityEvent, checkMessageSafety
} from '../security';
import { 
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  sendEmailVerification, sendPasswordResetEmail, signOut
} from 'firebase/auth';
import { 
  collection, doc, setDoc, getDoc, getDocs, addDoc,
  query, orderBy, onSnapshot, updateDoc, serverTimestamp, where, arrayUnion
} from 'firebase/firestore';

// ==================== Constants ====================

const APP_NAME = 'خالتي هيلة';
const SUPPORT_EMAIL = 'support@khaltihaila.com';
const CHAT_DURATION_DAYS = 7;
const MAX_RENEWALS = 4;
const COOLDOWN_HOURS = 48;
const PLATFORM_ACTIVE = false; // قائمة انتظار

const SESSION_KEYS = { 
  PROFILE: 'kh_profile_v1', 
  QUESTION: 'kh_question_v1',
  TUTORIAL_SEEN: 'kh_tutorial_seen'
};

const DEALBREAKER_OPTIONS = [
  'الالتزام الديني',
  'الاحترام المتبادل',
  'التفاهم الأسري',
  'الاستقرار المالي',
  'الطموح',
  'الصراحة',
  'نمط حياة هادئ',
  'تقبّل الاختلاف',
  'أخرى'
];

const WITHDRAWAL_MESSAGES = [
  'شكراً لوقتك، أتمنى لك التوفيق في رحلتك 🌹',
  'قررت المضي قدماً، أتمنى لك كل الخير 💫',
  'أشكرك على المحادثة، وفقك الله ✨'
];

// ==================== Questions ====================

const getQuestions = (gender, previouslyMarried, nationality) => {
  const nationalityQuestion = {
    id: 'nationality',
    textAr: 'ما جنسيتك؟',
    options: ['سعودي', 'غير سعودي'],
    required: true,
    isNationalityCheck: true
  };

  if (nationality === 'غير سعودي') {
    return [nationalityQuestion];
  }

  const baseQuestions = [
    nationalityQuestion,
    { id: 'phone', textAr: 'ما رقم جوالك؟', type: 'phone', placeholder: '05xxxxxxxx', hint: '🔒 مشفر - للتواصل بعد الموافقة فقط', required: true },
    { id: 'instagram', textAr: 'هل لديك حساب انستقرام؟', type: 'instagram' },
    { id: 'dob', textAr: 'ما تاريخ ميلادك؟', type: 'date', required: true },
    { id: 'gender', textAr: 'ما جنسك؟', options: ['ذكر', 'أنثى'], required: true }
  ];

  const personalQuestions = [
    { id: 'city', textAr: 'في أي مدينة تسكن؟', options: ['الرياض', 'جدة', 'مكة', 'المدينة', 'الدمام', 'الخبر', 'الطائف', 'تبوك', 'أبها', 'بريدة', 'حائل', 'نجران', 'جازان', 'الجبيل', 'ينبع', 'غير ذلك'], required: true },
    { id: 'region', textAr: 'من أي منطقة أنت أصلاً؟', options: ['الشمال', 'الجنوب', 'الغرب', 'الشرق', 'نجد', 'القصيم'], required: true },
    { id: 'qabili', textAr: 'ما وضعك من ناحية القبيلة؟', options: ['قبيلي', 'غير قبيلي', 'مجهول الأبوين', 'أفضل عدم الإجابة'], required: true },
    { id: 'marriageTimeline', textAr: 'متى تفكر بالزواج؟', options: ['خلال 3 أشهر', 'خلال 6 أشهر', 'خلال سنة', 'غير محدد'], required: true },
    { id: 'previouslyMarried', textAr: 'هل سبق لك الزواج؟', options: ['نعم', 'لا'], required: true }
  ];

  // سؤال الأطفال - يظهر فقط إذا سبق الزواج
  const childrenQuestion = { id: 'hasChildren', textAr: 'هل لديك أطفال؟', options: ['نعم', 'لا'] };
  
  const smokingQuestion = { id: 'smoking', textAr: 'هل أنت مدخن؟', options: ['نعم', 'لا', 'أحياناً'] };
  const disabilityQuestion = { id: 'disability', textAr: 'هل لديك أي احتياجات خاصة؟', options: ['لا يوجد', 'صعوبة في الحركة', 'ضعف في البصر', 'صعوبة في السمع', 'أخرى', 'أفضل عدم الإجابة'], hint: '🔒 محمي - يُستخدم لتحسين التوافق' };

  // سؤال الالتزام الديني - مختلف للرجال والنساء
  const maleReligiousQuestion = { id: 'religious', textAr: 'كيف تصف التزامك بالصلاة؟', options: ['أصلي لكن مو على الوقت دائماً', 'أصلي على الوقت', 'أصلي جماعة دائماً'], required: true };
  const femaleReligiousQuestion = { id: 'religious', textAr: 'كيف تصفين التزامك الديني؟', type: 'scale', hint: '1 = قليل، 5 = ملتزمة جداً' };

  const malePhotoQuestion = { id: 'hasPhoto', textAr: 'هل تريد إضافة صورة شخصية؟', options: ['نعم', 'لا'], hint: '📷 اختياري - يزيد فرص التوافق', type: 'photoChoice' };
  const femaleHijabQuestion = { id: 'hijabType', textAr: 'ما نوع حجابك؟', options: ['نقاب', 'حجاب', 'بدون', 'أفضل عدم الإجابة'] };

  // أسئلة نمط الحياة الجديدة
  const weekendQuestion = { id: 'weekend', textAr: 'نهاية أسبوع مثالية بالنسبة لك تكون غالباً:', options: ['في البيت والراحة', 'مع العائلة', 'مع الأصدقاء', 'نشاط خارج المنزل', 'حسب المزاج'] };
  const socialLifeQuestion = { id: 'socialLife', textAr: 'هل تفضل حياة اجتماعية نشطة أم هادئة؟', options: ['نشطة (زيارات وتجمعات)', 'هادئة (خصوصية)', 'متوازنة'] };

  const lifestyleQuestions = [
    { id: 'education', textAr: 'ما مستواك التعليمي؟', options: ['ثانوي', 'دبلوم', 'بكالوريوس', 'ماجستير', 'دكتوراه'] },
    { id: 'funActivities', textAr: 'ماذا تفعل للترفيه؟', options: ['السفر', 'الرياضة', 'القراءة', 'الألعاب', 'التصوير', 'الأفلام', 'الطبخ', 'التسوق'], type: 'multiple' },
    { id: 'industry', textAr: 'في أي مجال تعمل؟', options: ['عسكري', 'صحة', 'تعليم', 'هندسة', 'تقنية', 'مالي', 'تجارة', 'حكومي', 'قطاع خاص', 'طالب', 'لا أعمل', 'غير ذلك'] },
    { id: 'income', textAr: 'ما نطاق دخلك الشهري؟', options: ['أقل من 5,000', '5,000 - 10,000', '10,000 - 20,000', '20,000 - 30,000', 'أكثر من 30,000', 'لا يوجد دخل'], hint: '🔒 لهدف التوافق فقط - لا يظهر للشريك' },
    { id: 'height', textAr: 'كم طولك بالسنتيمتر؟', type: 'number', min: 100, max: 220, placeholder: '170' },
    weekendQuestion,
    socialLifeQuestion,
    { id: 'aboutYou', textAr: 'حدثنا عن نفسك باختصار...', type: 'textarea', hint: 'اكتب ما يميزك وما تبحث عنه', maxLength: 300 },
    { id: 'dealbreakers', textAr: 'ما أهم الأمور التي لا يمكنك التنازل عنها في شريك حياتك؟', hint: 'اختر حتى 3', options: DEALBREAKER_OPTIONS, type: 'dealbreakers', maxSelect: 3 }
  ];

  const partnerPreferences = [
    { id: 'prefMarried', textAr: 'هل تقبل شريك سبق له الزواج؟', options: ['نعم', 'لا', 'حسب الظروف'], type: 'multiple' },
    { id: 'prefChildren', textAr: 'هل تقبل شريك لديه أطفال؟', options: ['نعم', 'لا', 'حسب الظروف'], type: 'multiple' },
    { id: 'prefQabili', textAr: 'ما تفضيلك بخصوص القبيلة؟', options: ['قبيلي فقط', 'غير قبيلي', 'لا يهم'], type: 'multiple' },
    { id: 'prefDisability', textAr: 'هل تقبل شريك من ذوي الاحتياجات الخاصة؟', options: ['نعم أقبل', 'صعوبة حركة', 'ضعف بصر', 'صعوبة سمع', 'لا أقبل'], type: 'multiple' },
    { id: 'prefRegion', textAr: 'تفضيل منطقة الشريك؟', options: ['الشمال', 'الجنوب', 'الغرب', 'الشرق', 'نجد', 'القصيم', 'لا يهم'], type: 'multiple', hint: '⚠️ لن يتم ترشيح أي أحد خارج المناطق المختارة' },
    { id: 'prefAgeRange', textAr: 'تفضيل عمر الشريك؟', options: ['أصغر مني', 'بنفس عمري', 'أكبر مني', 'لا يهم'], type: 'multiple' }
  ];

  const maleHijabPrefQuestion = { id: 'prefHijab', textAr: 'تفضيل حجاب الشريكة؟', options: ['منقبة', 'محجبة', 'بدون', 'لا يهم'], type: 'multiple' };

  let allQuestions = [...baseQuestions];
  
  if (gender) {
    allQuestions.push(...personalQuestions);
    
    // سؤال الأطفال يظهر فقط إذا سبق الزواج
    if (previouslyMarried === 'نعم') {
      allQuestions.push(childrenQuestion);
    }
    
    allQuestions.push(smokingQuestion);
    allQuestions.push(disabilityQuestion);
    
    // سؤال الالتزام الديني حسب الجنس
    if (gender === 'ذكر') {
      allQuestions.push(maleReligiousQuestion);
      allQuestions.push(malePhotoQuestion);
    }
    
    if (gender === 'أنثى') {
      allQuestions.push(femaleReligiousQuestion);
      allQuestions.push(femaleHijabQuestion);
    }
    
    allQuestions.push(...lifestyleQuestions);
    allQuestions.push(...partnerPreferences);
    
    if (gender === 'ذكر') {
      allQuestions.push(maleHijabPrefQuestion);
    }
  }
  
  return allQuestions;
};

// ==================== Helper Functions ====================

const getSession = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch { return defaultValue; }
};

const setSession = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};

const clearSession = () => {
  try {
    localStorage.removeItem(SESSION_KEYS.PROFILE);
    localStorage.removeItem(SESSION_KEYS.QUESTION);
  } catch {}
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const formatTimeRemaining = (endTime) => {
  const now = new Date();
  const end = endTime?.toDate ? endTime.toDate() : new Date(endTime);
  const diff = end - now;
  if (diff <= 0) return { text: 'انتهى', expired: true };
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 24) return { text: `${Math.floor(hours / 24)} يوم`, days: Math.floor(hours / 24), expired: false };
  return { text: `${hours} ساعة`, hours, expired: false };
};

const getDaysRemaining = (endDate) => {
  const now = new Date();
  const end = endDate?.toDate ? endDate.toDate() : new Date(endDate);
  return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
};

const maskName = (name) => name ? name.charAt(0) + '.' : '';

// ==================== Main Component ====================

export default function AuthApp({ onBack }) {
  // Auth State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState('welcome');
  
  // Profile State
  const [profile, setProfile] = useState(() => getSession(SESSION_KEYS.PROFILE, {}));
  const [currentQuestion, setCurrentQuestion] = useState(() => getSession(SESSION_KEYS.QUESTION, 0));
  
  // Input State
  const [userInput, setUserInput] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [customDealbreaker, setCustomDealbreaker] = useState('');
  
  // Auth Form State
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authFirstName, setAuthFirstName] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Chat State
  const [activeChat, setActiveChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [conversationSuggestions, setConversationSuggestions] = useState([]);
  
  // Match State
  const [matches, setMatches] = useState([]);
  const [cooldownEnd, setCooldownEnd] = useState(null);
  
  // UI State
  const [currentTab, setCurrentTab] = useState('home');
  const [inputError, setInputError] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [rating, setRating] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showInstagramInput, setShowInstagramInput] = useState(false);
  
  const chatEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const questions = getQuestions(profile.gender, profile.previouslyMarried, profile.nationality);

  // Save progress to localStorage
  useEffect(() => { setSession(SESSION_KEYS.PROFILE, profile); }, [profile]);
  useEffect(() => { setSession(SESSION_KEYS.QUESTION, currentQuestion); }, [currentQuestion]);

  // Auth listener
  useEffect(() => {
    if (!auth) { setLoading(false); return; }
    
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      try {
        if (currentUser) {
          setUser(currentUser);
          // Email verification disabled for now - allow users to proceed directly
          // if (!currentUser.emailVerified) { setScreen('verifyEmail'); setLoading(false); return; }
          
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const decryptedData = {
              ...userData,
              phone: decryptData(userData.phone),
              email: decryptData(userData.email),
              income: decryptData(userData.income)
            };
            
            // فك تشفير lastName فقط إذا كان موجوداً
            if (userData.lastName) {
              decryptedData.lastName = decryptData(userData.lastName);
            }
            
            setProfile(decryptedData);
            clearSession();
            if (userData.cooldownEnd) {
              const { expired } = formatTimeRemaining(userData.cooldownEnd);
              if (!expired) setCooldownEnd(userData.cooldownEnd);
            }
            setScreen(PLATFORM_ACTIVE ? 'main' : 'waitlist');
            setCurrentTab('home');
          } else {
            const saved = getSession(SESSION_KEYS.PROFILE, {});
            setProfile(saved.firstName ? saved : { firstName: '', email: currentUser.email });
            setScreen('questions');
          }
        } else {
          setUser(null);
          setScreen('welcome');
        }
      } catch (error) {
        console.error('Auth error:', error);
        setScreen('error');
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe?.();
  }, []);

  // ==================== Auth Handlers ====================

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    const email = sanitizeInput(authEmail).toLowerCase();
    const name = sanitizeInput(authFirstName);
    
    if (!isValidEmail(email)) { setAuthError('بريد إلكتروني غير صحيح'); return; }
    if (authPassword.length < 6) { setAuthError('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return; }
    
    const rl = authRateLimiter.check(email);
    if (!rl.allowed) { setAuthError(`محاولات كثيرة، انتظر ${rl.waitTime} ثانية`); return; }
    
    setLoading(true);
    try {
      if (authMode === 'signup') {
        if (!name || name.length < 2) { setAuthError('أدخل اسمك الأول'); setLoading(false); return; }
        await createUserWithEmailAndPassword(auth, email, authPassword);
        
        // Email verification disabled for now - proceed directly to questions
        // Users can register and start answering questions immediately
        
        setProfile({ firstName: name, email });
        setScreen('questions');
      } else {
        await signInWithEmailAndPassword(auth, email, authPassword);
      }
      authRateLimiter.reset(email);
    } catch (error) {
      setAuthError(getAuthErrorMessage(error.code));
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!isValidEmail(authEmail)) { setAuthError('أدخل بريدك الإلكتروني أولاً'); return; }
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, authEmail);
      alert('تم إرسال رابط إعادة التعيين لبريدك');
      setShowForgotPassword(false);
    } catch (error) {
      setAuthError(getAuthErrorMessage(error.code));
    } finally { setLoading(false); }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setProfile({});
      setMatches([]);
      setActiveChat(null);
      setChatHistory([]);
      setCurrentQuestion(0);
      clearSession();
      setScreen('welcome');
      setCurrentTab('home');
    } catch (error) { console.error('Logout error:', error); }
  };

  // ==================== Match Loading ====================

  const loadMatches = useCallback(async () => {
    if (!user || !profile.gender || !PLATFORM_ACTIVE) return;
    
    try {
      setLoading(true);
      
      // جلب جميع المستخدمين من الجنس الآخر
      const usersRef = collection(db, 'users');
      const oppositeGender = profile.gender === 'ذكر' ? 'أنثى' : 'ذكر';
      const q = query(usersRef, where('gender', '==', oppositeGender));
      const snapshot = await getDocs(q);
      
      const candidates = [];
      snapshot.forEach((docSnap) => {
        const candidate = { id: docSnap.id, ...docSnap.data() };
        
        // التحقق من التوافق الكامل باستخدام checkFullCompatibility
        const compatibility = checkFullCompatibility(profile, candidate);
        if (compatibility.compatible) {
          candidates.push(candidate);
        }
      });
      
      // استخدام AI لتقييم التوافق
      if (candidates.length > 0) {
        const matchesWithScores = await generateMatchRecommendations(profile, candidates);
        setMatches(matchesWithScores);
      } else {
        setMatches([]);
      }
    } catch (error) {
      console.error('Load matches error:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [user, profile]);

  // تحميل المطابقات عند تحميل البروفايل
  useEffect(() => {
    if (user && profile.gender && PLATFORM_ACTIVE && screen === 'main') {
      loadMatches();
    }
  }, [user, profile.gender, screen, loadMatches]);

  // ==================== Question Handlers ====================

  const handleAnswer = async () => {
    const q = questions[currentQuestion];
    if (!q) return;
    setInputError('');
    
    // Nationality check
    if (q.isNationalityCheck && userInput === 'غير سعودي') {
      setProfile({ ...profile, nationality: 'غير سعودي' });
      setScreen('waitlistNonSaudi');
      return;
    }
    
    // التحقق من الأسئلة الإجبارية
    if (q.required) {
      if (q.type === 'multiple' || q.type === 'dealbreakers') {
        if (selectedOptions.length === 0) {
          setInputError('هذا السؤال إجباري - اختر خياراً واحداً على الأقل');
          return;
        }
      } else if (q.type === 'scale') {
        if (!userInput || userInput.trim() === '') {
          setInputError('هذا السؤال إجباري - اختر قيمة من 1 إلى 5');
          return;
        }
      } else if (q.type === 'instagram' || q.type === 'photoChoice') {
        // Instagram و photoChoice اختياريان حتى لو كان required
      } else if (q.options && !q.type) {
        if (!userInput || userInput.trim() === '') {
          setInputError('هذا السؤال إجباري - اختر خياراً');
          return;
        }
      } else {
        if (!userInput || userInput.trim() === '') {
          setInputError('هذا السؤال إجباري - لا يمكنك المتابعة دون الإجابة');
          return;
        }
      }
    }
    
    // Validations
    if (q.type === 'phone' && !isValidSaudiPhone(userInput)) { setInputError('رقم جوال سعودي غير صحيح'); return; }
    if (q.type === 'number') {
      const n = parseInt(userInput);
      if (isNaN(n) || n < q.min || n > q.max) { setInputError(`أدخل رقم بين ${q.min} و ${q.max}`); return; }
    }
    if (q.type === 'date') {
      if (!isValidAge(userInput)) { setInputError('يجب أن يكون عمرك 18 سنة أو أكثر'); return; }
    }
    
    let val;
    if (q.type === 'dealbreakers') {
      val = [...selectedOptions];
      if (selectedOptions.includes('أخرى') && customDealbreaker.trim()) {
        val = val.filter(o => o !== 'أخرى');
        val.push(customDealbreaker.trim());
      }
    } else if (q.type === 'multiple') {
      val = selectedOptions;
    } else {
      val = sanitizeInput(userInput);
    }
    
    const newProfile = { ...profile, [q.id]: val };
    setProfile(newProfile);
    
    // Special handling for dynamic questions
    if (q.id === 'gender' || q.id === 'previouslyMarried' || q.id === 'nationality') {
      const newQuestions = getQuestions(
        q.id === 'gender' ? val : newProfile.gender,
        q.id === 'previouslyMarried' ? val : newProfile.previouslyMarried,
        q.id === 'nationality' ? val : newProfile.nationality
      );
      
      let nextIndex = currentQuestion + 1;
      if (q.id === 'gender' && newQuestions.length > 6) {
        const cityIndex = newQuestions.findIndex(qq => qq.id === 'city');
        if (cityIndex !== -1) nextIndex = cityIndex;
      }
      if (nextIndex >= newQuestions.length) nextIndex = Math.max(0, newQuestions.length - 1);
      
      setCurrentQuestion(nextIndex);
      setUserInput('');
      setSelectedOptions([]);
      setCustomDealbreaker('');
      return;
    }
    
    // Next or save
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setUserInput('');
      setSelectedOptions([]);
      setCustomDealbreaker('');
    } else {
      // Save profile
      try {
        setLoading(true);
        const profileToSave = {
          ...newProfile,
          phone: encryptData(formatPhone(newProfile.phone)),
          email: encryptData(user.email),
          income: encryptData(newProfile.income),
        };
        
        // إضافة lastName فقط إذا كان موجوداً
        if (newProfile.lastName) {
          profileToSave.lastName = encryptData(newProfile.lastName);
        }
        
        await setDoc(doc(db, 'users', user.uid), {
          ...profileToSave,
          stats: { matchCount: 0, acceptCount: 0, rejectCount: 0, avgRating: 0, ratingCount: 0, memberSince: serverTimestamp() },
          withdrawnFrom: [],
          blockedUsers: [],
          createdAt: serverTimestamp(),
          userId: user.uid
        });
        clearSession();
        setScreen(PLATFORM_ACTIVE ? 'main' : 'waitlist');
        setCurrentTab('home');
      } catch (error) {
        console.error('Save profile error:', error);
        setInputError('خطأ في حفظ البيانات');
      } finally { setLoading(false); }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setUserInput('');
      setSelectedOptions([]);
      setInputError('');
    }
  };

  const handleSkipQuestion = () => {
    const q = questions[currentQuestion];
    if (q && !q.required) {
      setCurrentQuestion(currentQuestion + 1);
      setUserInput('');
      setSelectedOptions([]);
      setInputError('');
    }
  };

  // ==================== RENDER: Welcome ====================

  const renderWelcome = () => (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-6 md:p-8 space-y-5">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-rose-600 text-sm">
          <ArrowRight className="w-4 h-4" /> العودة للموقع
        </button>
        
        <div className="text-center">
          <div className="text-5xl mb-2">👩‍🦳💕</div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-rose-600 via-red-600 to-purple-600 bg-clip-text text-transparent">{APP_NAME}</h1>
          <p className="text-gray-600 mt-1">تربط راسين بالحلال</p>
        </div>

        <div className="flex justify-center">
          <div className="bg-purple-100 px-4 py-1.5 rounded-full flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">مدعوم بالذكاء الاصطناعي</span>
          </div>
        </div>

        <div className="bg-rose-50 rounded-xl p-3 text-right border border-rose-100">
          <div className="space-y-1 text-sm text-gray-700">
            <p>✓ للتعارف بهدف <strong className="text-rose-600">الزواج</strong></p>
            <p>✓ <strong className="text-rose-600">للسعوديين</strong> فقط</p>
            <p>✓ <strong className="text-rose-600">18 سنة</strong> فأكثر</p>
          </div>
        </div>

        <form onSubmit={handleAuth} className="space-y-3">
          {authMode === 'signup' && (
            <input type="text" value={authFirstName} onChange={e => setAuthFirstName(e.target.value)} placeholder="الاسم الأول" className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:outline-none" maxLength={50} />
          )}
          <input type="email" value={authEmail} onChange={e => setAuthEmail(e.target.value.toLowerCase())} placeholder="البريد الإلكتروني" className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:outline-none" dir="ltr" />
          <input type="password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} placeholder="كلمة المرور" className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:outline-none" />
          
          {authMode === 'login' && (
            <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-rose-600 hover:underline">نسيت كلمة المرور؟</button>
          )}
          
          {authError && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />{authError}
            </div>
          )}

          <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-rose-500 via-red-500 to-purple-600 text-white py-3 rounded-xl font-bold disabled:opacity-50 transition-all active:scale-95">
            {loading ? <Loader className="w-5 h-5 mx-auto animate-spin" /> : authMode === 'login' ? 'تسجيل الدخول' : 'إنشاء حساب'}
          </button>
        </form>

        <button onClick={() => { setAuthMode(authMode === 'login' ? 'signup' : 'login'); setAuthError(''); }} className="w-full text-gray-600 hover:text-rose-600 text-sm underline">
          {authMode === 'login' ? 'ليس لديك حساب؟ سجل الآن' : 'لديك حساب؟ سجل الدخول'}
        </button>

        <div className="text-center">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1">
            <HelpCircle className="w-3 h-3" /> الدعم: {SUPPORT_EMAIL}
          </a>
        </div>
      </div>
      
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-bold">استعادة كلمة المرور</h3>
            <input type="email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} placeholder="البريد الإلكتروني" className="w-full p-3 border-2 border-gray-200 rounded-xl" dir="ltr" />
            <div className="flex gap-3">
              <button onClick={() => setShowForgotPassword(false)} className="flex-1 py-2 border-2 border-gray-200 rounded-xl">إلغاء</button>
              <button onClick={handleForgotPassword} className="flex-1 py-2 bg-rose-500 text-white rounded-xl">إرسال</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ==================== RENDER: Verify Email ====================

  const renderVerifyEmail = () => (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center space-y-6">
        <Mail className="w-16 h-16 text-rose-500 mx-auto" />
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">تحقق من بريدك</h2>
          <p className="text-gray-600 text-sm">تم إرسال رسالة تحقق إلى</p>
          <p className="font-bold text-rose-600">{user?.email || authEmail}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-right space-y-2">
          <p className="text-amber-800 text-sm font-medium">📧 تم إرسال رسالة التحقق إلى:</p>
          <p className="text-amber-900 font-bold text-base">{user?.email || authEmail}</p>
          <div className="mt-3 space-y-1">
            <p className="text-amber-800 text-sm">✓ افتح الرابط في بريدك للمتابعة</p>
            <p className="text-amber-700 text-xs">💡 تحقق من مجلد <strong>Spam</strong> أو <strong>Junk</strong></p>
            <p className="text-amber-700 text-xs">⏱️ قد تستغرق الرسالة دقيقة أو دقيقتين</p>
            <p className="text-amber-700 text-xs">🔄 إذا لم تصل، اضغط "إعادة الإرسال"</p>
          </div>
        </div>
        <div className="space-y-2">
          <button onClick={async () => { 
            if (!user) return;
            try {
              setLoading(true);
              await sendEmailVerification(user, {
                url: `${window.location.origin}/#/app`,
                handleCodeInApp: false
              });
              alert('✅ تم إرسال رسالة التحقق - تحقق من بريدك (وSpam أيضاً)');
            } catch (e) {
              console.error('خطأ في إعادة الإرسال:', e);
              alert(`⚠️ خطأ: ${e.message || 'انتظر قليلاً ثم حاول مرة أخرى'}`);
            } finally {
              setLoading(false);
            }
          }} className="w-full border-2 border-rose-500 text-rose-600 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50" disabled={loading}>
            <RefreshCw className="w-4 h-4" />إعادة الإرسال
          </button>
          <button onClick={async () => { 
            if (user) { 
              await user.reload(); 
              const d = await getDoc(doc(db, 'users', user.uid)); 
              setScreen(d.exists() ? (PLATFORM_ACTIVE ? 'main' : 'waitlist') : 'questions'); 
            }
          }} className="w-full bg-gradient-to-r from-rose-500 to-purple-500 text-white py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2">
            <Check className="w-4 h-4" />متابعة
          </button>
        </div>
        <button onClick={handleLogout} className="text-gray-500 hover:text-red-600 text-sm flex items-center justify-center gap-2 mx-auto">
          <LogOut className="w-4 h-4" />تسجيل الخروج
        </button>
      </div>
    </div>
  );

  // ==================== RENDER: Waitlist ====================

  const renderWaitlist = () => (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center space-y-6">
        <div className="text-6xl">🎉</div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">تم تسجيلك بنجاح!</h2>
          <p className="text-gray-600">أنت الآن في قائمة الانتظار</p>
        </div>
        
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-right">
          <p className="text-gray-700 text-sm">نؤمن أن التعارف الجاد يحتاج مجتمع جاد.</p>
          <p className="text-gray-700 text-sm mt-2">سنرسل لك إيميل فور إطلاق المنصة 💌</p>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">🚀 ساعدنا نوصل أسرع!</p>
          
          <button onClick={() => { navigator.clipboard.writeText(window.location.origin); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }} className="w-full border-2 border-rose-500 text-rose-600 py-3 rounded-xl font-semibold flex items-center justify-center gap-2">
            {copiedLink ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            {copiedLink ? 'تم النسخ!' : 'نسخ رابط الدعوة'}
          </button>
          
          <button onClick={() => { const text = `👩‍🦳💕 خالتي هيلة - منصة للتعارف بهدف الزواج\n\n✨ مدعومة بالذكاء الاصطناعي\n🔒 آمنة ومحمية\n\nسجّل الآن:\n${window.location.origin}`; window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); }} className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2">
            <Share2 className="w-5 h-5" /> مشاركة واتساب
          </button>
        </div>

        <p className="text-sm text-gray-500">📧 شيّك إيميلك بشكل دوري</p>

        <button onClick={handleLogout} className="text-gray-500 hover:text-red-600 text-sm flex items-center justify-center gap-2 mx-auto">
          <LogOut className="w-4 h-4" />تسجيل الخروج
        </button>
        
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1">
          <HelpCircle className="w-3 h-3" /> الدعم
        </a>
      </div>
    </div>
  );

  // ==================== RENDER: Questions ====================

  const renderQuestions = () => {
    const q = questions[currentQuestion];
    if (!q) return null;
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const questionsRemaining = questions.length - currentQuestion - 1;

    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-purple-50 p-4">
        <div className="max-w-lg mx-auto pt-4">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-gray-500">مرحباً {profile.firstName || ''}</span>
            <button onClick={handleLogout} className="text-gray-500 hover:text-red-600 flex items-center gap-1 text-sm">
              <LogOut className="w-4 h-4" />خروج
            </button>
          </div>

          <div className="mb-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-500 to-purple-500 transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs text-gray-500 mt-1 text-center">{currentQuestion + 1} من {questions.length} • باقي {questionsRemaining} سؤال</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-lg">👩‍🦳</span>
              </div>
              <div className="flex-1">
                <p className="text-lg font-medium text-gray-900">{q.textAr}</p>
                {q.hint && <p className="text-xs text-gray-500 mt-1">{q.hint}</p>}
              </div>
            </div>

            {inputError && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />{inputError}
              </div>
            )}

            {/* Question Types */}
            {q.type === 'phone' && (
              <input type="tel" value={userInput} onChange={e => setUserInput(e.target.value.replace(/[^0-9+]/g, ''))} placeholder={q.placeholder} className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-rose-500 focus:outline-none" dir="ltr" maxLength={13} />
            )}

            {q.type === 'instagram' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { setShowInstagramInput(false); setUserInput('لا يوجد'); }} className={`p-3 border-2 rounded-xl text-sm ${userInput === 'لا يوجد' ? 'border-rose-500 bg-rose-50' : 'border-gray-200'}`}>لا يوجد</button>
                  <button onClick={() => setShowInstagramInput(true)} className={`p-3 border-2 rounded-xl text-sm ${showInstagramInput ? 'border-rose-500 bg-rose-50' : 'border-gray-200'}`}>يوجد</button>
                </div>
                {showInstagramInput && <input type="text" value={userInput} onChange={e => setUserInput(e.target.value)} placeholder="@username" className="w-full p-3 border-2 border-gray-200 rounded-xl" dir="ltr" maxLength={50} />}
              </div>
            )}

            {q.type === 'number' && <input type="number" value={userInput} onChange={e => setUserInput(e.target.value.slice(0, 3))} placeholder={q.placeholder} min={q.min} max={q.max} className="w-full p-3 border-2 border-gray-200 rounded-xl text-center" dir="ltr" />}

            {q.type === 'date' && <input type="date" value={userInput} onChange={e => setUserInput(e.target.value)} max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]} className="w-full p-3 border-2 border-gray-200 rounded-xl" />}

            {q.type === 'scale' && (
              <div className="flex justify-between gap-2">
                {[1,2,3,4,5].map(n => (
                  <button key={n} onClick={() => setUserInput(n.toString())} className={`flex-1 h-12 border-2 rounded-xl font-bold text-lg ${userInput === n.toString() ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200'}`}>{n}</button>
                ))}
              </div>
            )}

            {q.type === 'textarea' && (
              <div>
                <textarea value={userInput} onChange={e => setUserInput(e.target.value)} rows={3} maxLength={q.maxLength || 300} placeholder="اكتب هنا..." className="w-full p-3 border-2 border-gray-200 rounded-xl resize-none" />
                <p className="text-xs text-gray-400 text-left">{userInput.length}/{q.maxLength || 300}</p>
              </div>
            )}

            {q.type === 'text' && <input type="text" value={userInput} onChange={e => setUserInput(e.target.value)} placeholder="اكتب هنا..." className="w-full p-3 border-2 border-gray-200 rounded-xl" maxLength={100} />}

            {q.type === 'multiple' && (
              <div className="grid grid-cols-2 gap-2">
                {q.options.map((o, i) => (
                  <button key={i} onClick={() => setSelectedOptions(selectedOptions.includes(o) ? selectedOptions.filter(x => x !== o) : [...selectedOptions, o])} className={`p-3 border-2 rounded-xl text-sm text-right ${selectedOptions.includes(o) ? 'border-rose-500 bg-rose-50' : 'border-gray-200'}`}>
                    {o}{selectedOptions.includes(o) && <Check className="inline w-4 h-4 text-rose-500 mr-1" />}
                  </button>
                ))}
              </div>
            )}

            {q.type === 'dealbreakers' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {q.options.map((o, i) => (
                    <button key={i} onClick={() => { if (selectedOptions.includes(o)) setSelectedOptions(selectedOptions.filter(x => x !== o)); else if (selectedOptions.length < q.maxSelect) setSelectedOptions([...selectedOptions, o]); }} disabled={!selectedOptions.includes(o) && selectedOptions.length >= q.maxSelect} className={`p-3 border-2 rounded-xl text-sm text-right ${selectedOptions.includes(o) ? 'border-rose-500 bg-rose-50' : 'border-gray-200'} ${!selectedOptions.includes(o) && selectedOptions.length >= q.maxSelect ? 'opacity-50' : ''}`}>
                      {o}{selectedOptions.includes(o) && <Check className="inline w-4 h-4 text-rose-500 mr-1" />}
                    </button>
                  ))}
                </div>
                {selectedOptions.includes('أخرى') && <input type="text" value={customDealbreaker} onChange={e => setCustomDealbreaker(e.target.value)} placeholder="اكتب الأمر المهم لك..." className="w-full p-3 border-2 border-gray-200 rounded-xl" maxLength={100} />}
                <p className="text-xs text-gray-500 text-center">{selectedOptions.length}/{q.maxSelect} مختار</p>
              </div>
            )}

            {q.options && !q.type && (
              <div className="grid gap-2">
                {q.options.map((o, i) => (
                  <button key={i} onClick={() => setUserInput(o)} className={`p-3 border-2 rounded-xl text-right ${userInput === o ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`}>{o}</button>
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-2 pt-2">
              {currentQuestion > 0 && (
                <button onClick={handlePreviousQuestion} className="flex-1 py-3 border-2 border-gray-200 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-50">
                  <ChevronRight className="w-5 h-5" /> السابق
                </button>
              )}
              <button onClick={handleAnswer} disabled={loading} className="flex-1 bg-gradient-to-r from-rose-500 to-purple-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95">
                {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'التالي'}
                {!loading && <ChevronLeft className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER: Error ====================

  const renderError = () => (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center space-y-6">
        <div className="text-6xl">😕</div>
        <h2 className="text-xl font-bold text-gray-900">عذراً!</h2>
        <p className="text-gray-600">حدث خطأ غير متوقع</p>
        <div className="space-y-2">
          <button onClick={() => window.location.reload()} className="w-full bg-gradient-to-r from-rose-500 to-purple-500 text-white py-3 rounded-xl font-semibold">🔄 تحديث الصفحة</button>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="block w-full border-2 border-gray-200 text-gray-700 py-3 rounded-xl font-semibold">📧 تواصل مع الدعم</a>
        </div>
      </div>
    </div>
  );

  // ==================== MAIN RETURN ====================

  if (loading && !screen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">👩‍🦳💕</div>
          <Heart className="w-8 h-8 text-rose-500 animate-pulse mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans" dir="rtl">
      {screen === 'welcome' && renderWelcome()}
      {screen === 'verifyEmail' && renderVerifyEmail()}
      {screen === 'waitlist' && renderWaitlist()}
      {screen === 'waitlistNonSaudi' && renderWaitlist()}
      {screen === 'questions' && renderQuestions()}
      {screen === 'main' && renderWaitlist()} {/* Placeholder - full main screen in production */}
      {screen === 'error' && renderError()}
    </div>
  );
}
